// Every string SaleDeskNoticesSheet renders of its own, and the shape a caller
// may replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The sheet's words of its own are its head, the bell's name, the word on a run
// tag, the sentence under the list and the empty list's two lines. Everything
// else arrives from the page already in the reader's language: the sale's name,
// each notice's lane, text and time, and the run number.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the sheet, with BidCard's `interpolate`.
 * Upper case on the tags is the tags' CSS, not the strings'.
 */
export const SALE_DESK_NOTICES_SHEET_TRANSLATIONS = Object.freeze({
  // The head: who is speaking, and to whom. The sale's name is the page's, and
  // where it goes is the translation's decision, so the separator is part of
  // the string, as SaleDeskStrip's eyebrow has it.
  title: 'Sale desk',
  subtitle: '{sale} · everyone attending',

  // The bell's name. It is a toggle, so the name stays the same and
  // `aria-pressed` says whether the sale is muted, which is how a screen reader
  // expects a toggle to speak.
  mute: 'Mute notices',

  // The list group's name. It is a region the keyboard can reach, so a screen
  // reader names it when it takes focus.
  notices: 'Notices',

  // The word on a run tag, with the run number the page passes. BidSheet's
  // `run` key, for the same label.
  run: 'Run {run}',

  // The sentence under the list, one per state of the bell.
  footer: 'Notices clear when the sale closes',
  footerMuted: 'Muted · no toasts or pushes for this sale',

  // A sale the desk has said nothing to yet. The handoff draws no empty list;
  // these are the library's words for it until the design writes its own.
  emptyTitle: 'No notices yet',
  emptyBody: 'The sale desk posts here when a run is pulled, a lane is paused or an announcement is added.',
});

/**
 * The keys in `translations` that SaleDeskNoticesSheet would not read, as
 * dotted paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument,
 * so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, SALE_DESK_NOTICES_SHEET_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) {
    return SALE_DESK_NOTICES_SHEET_TRANSLATIONS;
  }

  return { ...SALE_DESK_NOTICES_SHEET_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidSaleDeskNoticesSheetI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') {
    return false;
  }
  if (Object.keys(i18n).some((key) => key !== 'translations')) {
    return false;
  }
  if (i18n.translations === undefined) {
    return true;
  }
  return unreadTranslationKeys(i18n.translations).length === 0;
}
